import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { PRIORITY_COLORS, ROLES } from '../utils/config';

function getLocalDateStr(d = new Date()) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}


const ROLE_DEPARTMENT = {
  general_manager: '项目管理部',
  manager: '项目管理部',
  steward_chief: '客户服务部',
  steward: '客户服务部',
  engineer: '工程维修部',
  security: '秩序维护部',
  order_staff: '秩序维护部',
  fire_control: '消防监控部',
  cleaner_chief: '环境管理部',
  cleaner: '环境管理部',
  finance: '财务部'
};

const DEPARTMENT_ALIASES = {
  '项目管理部': ['项目管理部'],
  '客户服务部': ['客户服务部', '管家部'],
  '工程维修部': ['工程维修部', '工程部', '秩序工程部'],
  '秩序维护部': ['秩序维护部', '秩序部', '秩序工程部'],
  '消防监控部': ['消防监控部', '秩序部', '秩序工程部'],
  '环境管理部': ['环境管理部', '保洁部', '绿化部'],
  '财务部': ['财务部']
};

const departmentMatches = (standardDept, taskDept) =>
  !!standardDept && (DEPARTMENT_ALIASES[standardDept] || [standardDept]).includes(taskDept);
const SEE_ALL_TASK_ROLES = [
  'admin',
  'general_manager',
  'manager',
  'steward_chief',
  'finance'
];

const PRIORITY_ORDER = { '紧急': 0, '高': 1, '中': 2, '低': 3 };
const REVIEW_ROLES = ['admin','general_manager','manager','steward_chief','steward','finance'];
const ADD_TASK_ROLES = [
  'admin',
  'general_manager',
  'manager',
  'steward_chief',
  'finance',
  'steward',
  'engineer',
  'security',
  'cleaner',
  'fire_control',
  'order_staff'
];
const FEE_ROLES = ['admin','finance','manager','general_manager','steward','steward_chief'];
const COMMUNITY_ROLES = ['admin','general_manager','manager','steward_chief','steward'];
const VEHICLE_ROLES = ['admin','general_manager','manager','steward_chief','steward','security','fire_control','order_staff'];
const FACE_ROLES = ['admin','general_manager','manager','steward','engineer','finance','fire_control','order_staff'];

export default function Workbench({ user }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [weekView, setWeekView] = useState('today');
  const [weekTasks, setWeekTasks] = useState([]);

  const getWeekDates = (offset = 0) => {
    const today = new Date();
    const day = today.getDay() === 0 ? 7 : today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - day + 1 + offset * 7);
    return Array.from({length: 7}, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    });
  };

  const loadWeekTasks = async (offset = 0) => {
    const myDept = ROLE_DEPARTMENT[user.role];
    const isMine = (t) => {
      if (t.task_type === 'property_review') return REVIEW_ROLES.includes(user.role);
      if (SEE_ALL_TASK_ROLES.includes(user.role)) return true;
      if (t.assign_user_id === user.id) return true;
      if (!myDept) return true;
      return t.assign_type === 'department' && departmentMatches(myDept, t.department);
    };
    const dates = getWeekDates(offset);
    const results = await Promise.all(dates.map(date => API.get('/daily', { params: { date } }).catch(() => ({ data: [] }))));
    setWeekTasks(dates.map((date, i) => ({ date, tasks: results[i].data.filter(isMine) })));
  };

  useEffect(() => {
    const myDept = ROLE_DEPARTMENT[user.role];
    const isMine = (t) => {
      if (t.task_type === 'property_review') return REVIEW_ROLES.includes(user.role);
      if (SEE_ALL_TASK_ROLES.includes(user.role)) return true;
      if (t.assign_user_id === user.id) return true;
      if (!myDept) return true; // 管理/财务等无固定部门角色，看全部
      return t.assign_type === 'department' && departmentMatches(myDept, t.department);
    };

    const loadMain = () => Promise.all([
      API.get('/tasks').catch(() => ({ data: [] })),
      API.get('/daily', { params: { date: getLocalDateStr() } }).catch(() => ({ data: [] })),
      REVIEW_ROLES.includes(user.role) ? API.get('/property-archive/review-tasks').catch(() => ({data:[]})) : Promise.resolve({data:[]})
    ]).then(([taskRes, dailyRes, reviewRes]) => {
      const openTasks = [...reviewRes.data, ...taskRes.data]
        .filter(t => t.status !== '已完成')
        .filter(isMine)
        .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9) || new Date(b.created_at) - new Date(a.created_at));
      setTasks(openTasks);

      const myDaily = dailyRes.data.filter(isMine);
      setDailyTasks(myDaily);
    }).finally(() => setLoading(false));

    loadMain();
    const timer = setInterval(loadMain, 30000);
    return () => clearInterval(timer);
  }, [user.id, user.role]);

  const getLocation = (t) => {
    if (t.task_type === 'property_review') return t.review_room_no || t.public_area || '';
    if (t.location_type === '住宅') return `${t.building || ''}${t.unit || ''}${t.floor || ''}${t.room || ''}`;
    return t.public_area || '';
  };

  const doneDaily = dailyTasks.filter(t => t.status === '已完成');
  const undoneDaily = dailyTasks.filter(t => t.status !== '已完成');
  const visibleTasks = tasks.slice(0, 5);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div>
            <div style={styles.greeting}>您好，{user.real_name}</div>
            <div style={styles.roleTag}>{ROLES[user.role] || user.role}</div>
          </div>
        </div>
        <div style={styles.statRow}>
          <div style={styles.statPill}>
            <div style={styles.statNum}>{tasks.length}</div>
            <div style={styles.statLabel}>待办事项</div>
          </div>
          <div style={styles.statPill}>
            <div style={styles.statNum}>{doneDaily.length}/{dailyTasks.length}</div>
            <div style={styles.statLabel}>今日任务</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={styles.emptyHint}>加载中...</div>
      ) : (
        <>
          <div style={styles.quickRow}>
            {['admin','general_manager','manager','steward_chief','finance','steward','engineer','security','cleaner','fire_control','order_staff'].includes(user.role) && <button style={styles.quickBtn} onClick={() => navigate('/parcels')}><span style={styles.quickIcon}>📦</span>快递登记</button>}
            <button style={styles.quickBtn} onClick={() => window.open('/dashboard', '_blank')}>
              <span style={styles.quickIcon}>📺</span>物业看板
            </button>
            <button style={styles.quickBtn} onClick={() => navigate('/entertainment')}>
              <span style={styles.quickIcon}>🎮</span>休闲娱乐
            </button>
            {ADD_TASK_ROLES.includes(user.role) && (
              <button style={styles.quickBtn} onClick={() => navigate('/add')}>
                <span style={styles.quickIcon}>➕</span>新增事项
              </button>
            )}
            {FEE_ROLES.includes(user.role) && (
              <button style={styles.quickBtn} onClick={() => navigate('/fees')}>
                <span style={styles.quickIcon}>💰</span>物业费管理
              </button>
            )}
            {COMMUNITY_ROLES.includes(user.role) && (
              <button style={styles.quickBtn} onClick={() => navigate('/community')}>
                <span style={styles.quickIcon}>📣</span>物业通告与投票
              </button>
            )}
            {VEHICLE_ROLES.includes(user.role) && (
              <button style={styles.quickBtn} onClick={() => { window.location.href = 'https://cloud.tingzhihui.cn/#/openCard'; }}>
                <span style={styles.quickIcon}>🚗</span>车辆通行
              </button>
            )}
            {FACE_ROLES.includes(user.role) && (
              <button style={styles.quickBtn} onClick={() => navigate('/face-manage')}>
                <span style={styles.quickIcon}>🙂</span>门禁管理
              </button>
            )}

            {['admin','general_manager','manager','steward_chief','fire_control','order_staff'].includes(user.role) && (
              <button style={styles.quickBtn} onClick={() => navigate('/delivery-review')}>
                <span style={styles.quickIcon}>👤</span>人员审核
              </button>
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <div style={styles.sectionTitle}>📋 待办事项</div>
              <div style={styles.sectionMore} onClick={() => navigate('/tasks')}>查看全部 ›</div>
            </div>
            {visibleTasks.length === 0 ? (
              <div style={styles.emptyHint}>暂无待办事项</div>
            ) : (
              visibleTasks.map(t => {
                const urgencyClass = t.priority === '紧急' ? styles.cardUrgent : t.priority === '高' ? styles.cardHigh : styles.cardNormal;
                return (
                  <div key={t.id} style={{...styles.taskCard, ...urgencyClass}} onClick={() => navigate(t.task_type === 'property_review' ? `/review-task/${t.review_request_id}` : `/task/${t.id}`)}>
                    <div style={styles.taskBody}>
                      <div style={styles.taskTitleRow}>
                        <div style={styles.taskTitle}>{t.title}</div>
                        <span style={{...styles.priorityBadge, background: PRIORITY_COLORS[t.priority]+'22', color: PRIORITY_COLORS[t.priority]}}>{t.priority}</span>
                      </div>
                      <div style={styles.taskMeta}>📍 {getLocation(t)} · {t.source}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <div style={styles.sectionTitle}>✅ {weekView==='week' ? '本周任务' : weekView==='nextweek' ? '下周任务' : '今日任务'}</div>
              <div style={{display:'flex', gap:'6px', alignItems:'center'}}>
                <button style={{fontSize:'12px', padding:'3px 10px', borderRadius:'10px', border:'1px solid #d9d9d9', background: weekView==='today' ? '#fa8c16' : '#fff', color: weekView==='today' ? '#fff' : '#666', cursor:'pointer'}} onClick={() => setWeekView('today')}>今日</button>
                <button style={{fontSize:'12px', padding:'3px 10px', borderRadius:'10px', border:'1px solid #d9d9d9', background: weekView==='week' ? '#fa8c16' : '#fff', color: weekView==='week' ? '#fff' : '#666', cursor:'pointer'}} onClick={() => { setWeekView('week'); loadWeekTasks(0); }}>本周</button>
                <button style={{fontSize:'12px', padding:'3px 10px', borderRadius:'10px', border:'1px solid #d9d9d9', background: weekView==='nextweek' ? '#fa8c16' : '#fff', color: weekView==='nextweek' ? '#fff' : '#666', cursor:'pointer'}} onClick={() => { setWeekView('nextweek'); loadWeekTasks(1); }}>下周</button>
                <div style={styles.sectionMore} onClick={() => navigate('/daily')}>全部 ›</div>
              </div>
            </div>
            {weekView==='today' ? (
              dailyTasks.length === 0 ? (
                <div style={styles.emptyHint}>今日暂无分派任务</div>
              ) : (
                <div style={styles.dailyCard}>
                  {undoneDaily.length === 0 ? (
                    <div style={{...styles.emptyHint, padding: '14px 0'}}>🎉 今日任务已全部完成</div>
                  ) : (
                    undoneDaily.map(t => (
                      <div key={t.id} style={styles.dailyItem} onClick={() => navigate('/daily')}>
                        <div style={styles.checkbox}></div>
                        <div style={styles.dailyText}>
                          <div style={styles.dailyName}>{t.title}</div>
                          <div style={styles.dailyTime}>{t.status === '已超时' ? '⚠ 已超时' : `建议 ${t.scheduled_time} 前完成`}</div>
                        </div>
                      </div>
                    ))
                  )}
                  {doneDaily.length > 0 && (
                    <div style={styles.doneToggle} onClick={() => setShowDone(!showDone)}>
                      {showDone ? '收起已完成 ▲' : `已完成 ${doneDaily.length} 项 ▼`}
                    </div>
                  )}
                  {showDone && doneDaily.map(t => (
                    <div key={t.id} style={styles.dailyItem}>
                      <div style={{...styles.checkbox, ...styles.checkboxDone}}>✓</div>
                      <div style={styles.dailyText}>
                        <div style={{...styles.dailyName, ...styles.dailyNameDone}}>{t.title}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div style={styles.dailyCard}>
                {weekTasks.map(({date, tasks: dayTasks}) => {
                  const weekLabels = ['周一','周二','周三','周四','周五','周六','周日'];
                  const d = new Date(date);
                  const dayIndex = d.getDay() === 0 ? 6 : d.getDay() - 1;
                  const isToday = weekView !== 'nextweek' && date === getLocalDateStr();
                  const done = dayTasks.filter(t => t.status === '已完成').length;
                  return (
                    <div key={date} style={{marginBottom:"10px"}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background: isToday ? '#fff7e6' : '#f5f5f5', borderRadius:'6px', marginBottom:'4px'}}>
                        <span style={{fontSize:'13px', fontWeight:600, color: isToday ? '#fa8c16' : '#666'}}>{weekLabels[dayIndex]} {date.slice(5)} {isToday ? '（今天）' : ''}</span>
                        <span style={{fontSize:'12px', color:'#999'}}>{done}/{dayTasks.length} 完成</span>
                      </div>
                      {dayTasks.length === 0 ? (
                        <div style={{fontSize:'12px', color:'#ccc', padding:'4px 10px'}}>无任务</div>
                      ) : dayTasks.map(t => (
                        <div key={t.id} style={{...styles.dailyItem, opacity: t.status==='已完成' ? 0.6 : 1}} onClick={() => navigate('/daily')}>
                          <div style={t.status==='已完成' ? {...styles.checkbox, ...styles.checkboxDone} : styles.checkbox}>{t.status==='已完成' ? '✓' : ''}</div>
                          <div style={styles.dailyText}>
                            <div style={t.status==='已完成' ? {...styles.dailyName,...styles.dailyNameDone} : styles.dailyName}>{t.title}</div>
                            <div style={styles.dailyTime}>{t.status==='已超时' ? '⚠ 已超时' : t.status==='已完成' ? '✅ 已完成' : t.scheduled_time}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f5f7fa', paddingBottom: '20px' },
  header: { background: 'linear-gradient(90deg, #1a4a8a, #2d7dd2)', padding: '20px 16px 22px' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  greeting: { color: '#fff', fontSize: '18px', fontWeight: 800 },
  roleTag: { color: 'rgba(255,255,255,0.8)', fontSize: '12px', marginTop: '4px' },
  statRow: { display: 'flex', gap: '10px', marginTop: '16px' },
  statPill: { flex: 1, background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '10px 12px', textAlign: 'center' },
  statNum: { color: '#fff', fontSize: '20px', fontWeight: 800, lineHeight: 1 },
  statLabel: { color: 'rgba(255,255,255,0.85)', fontSize: '11px', marginTop: '4px' },
  section: { padding: '16px 16px 0' },
  quickRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '10px', padding: '14px 16px 0' },
  quickBtn: { minWidth: 0, background: '#fff', border: 'none', borderRadius: '12px', padding: '12px 8px', fontSize: '13px', fontWeight: 700, color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' },
  quickIcon: { fontSize: '20px' },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  sectionTitle: { fontSize: '15px', fontWeight: 800, color: '#222' },
  sectionMore: { fontSize: '12px', color: '#999', cursor: 'pointer' },
  emptyHint: { textAlign: 'center', color: '#bbb', fontSize: '13px', padding: '24px 0' },
  taskCard: { background: '#fff', borderRadius: '12px', padding: '12px 14px', marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderLeft: '4px solid #ddd', cursor: 'pointer' },
  cardUrgent: { borderLeftColor: '#ff4d4f' },
  cardHigh: { borderLeftColor: '#ff7a45' },
  cardNormal: { borderLeftColor: '#1890ff' },
  taskBody: { flex: 1, minWidth: 0 },
  taskTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  taskTitle: { fontSize: '14px', fontWeight: 700, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 },
  priorityBadge: { fontSize: '10px', padding: '2px 7px', borderRadius: '8px', flexShrink: 0, fontWeight: 600 },
  taskMeta: { fontSize: '12px', color: '#999', marginTop: '4px' },
  dailyCard: { background: '#fff', borderRadius: '12px', padding: '4px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '14px' },
  dailyItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f2f2f2', cursor: 'pointer' },
  checkbox: { width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #d9d9d9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#fff' },
  checkboxDone: { background: '#52c41a', borderColor: '#52c41a' },
  dailyText: { flex: 1 },
  dailyName: { fontSize: '14px', color: '#333', fontWeight: 600 },
  dailyNameDone: { color: '#bbb', textDecoration: 'line-through' },
  dailyTime: { fontSize: '11px', color: '#aaa', marginTop: '2px' },
  doneToggle: { textAlign: 'center', fontSize: '12px', color: '#1a4a8a', padding: '10px 0', cursor: 'pointer' }
};
